/**
 * @module collectionRequestStatusLabels
 * @purpose Translated labels for the two-dimension collection-request status model (packageStatus/returnStatus).
 * @depends react-i18next TFunction, PackageStatus/ReturnStatus/ALL_* types
 * @dependedOnBy PackageReturnStatusBadges, CollectionRequestsView, StatusOverrideControl
 *
 * @rules
 * - State: none -- pure lookup functions (rules-as-data)
 * - Errors: n/a
 */

import type { TFunction } from 'i18next'
import {
  ALL_PACKAGE_STATUSES,
  ALL_RETURN_STATUSES,
  type PackageStatus,
  type ReturnStatus,
} from '../../../types/collection-requests'

export function packageStatusLabel(t: TFunction<'cases'>, status: PackageStatus): string {
  const labels: Record<PackageStatus, string> = {
    draft: t('collections.packageStatusDraft'),
    pending: t('collections.packageStatusPending'),
    generating: t('collections.packageStatusGenerating'),
    ready: t('collections.packageStatusReady'),
    sent: t('collections.packageStatusSent'),
    stalled: t('collections.packageStatusStalled'),
    ingested: t('collections.packageStatusIngested'),
    failed: t('collections.packageStatusFailed'),
    cancelled: t('collections.packageStatusCancelled'),
    expired: t('collections.packageStatusExpired'),
  }
  return labels[status]
}

export function returnStatusLabel(
  t: TFunction<'cases'>,
  status: Exclude<ReturnStatus, 'none'>
): string {
  const labels: Record<Exclude<ReturnStatus, 'none'>, string> = {
    partially_returned: t('collections.returnStatusPartiallyReturned'),
    fully_returned: t('collections.returnStatusFullyReturned'),
    completed: t('collections.returnStatusCompleted'),
  }
  return labels[status]
}

export function returnStatusLabelAny(t: TFunction<'cases'>, status: ReturnStatus): string {
  if (status === 'none') return t('collections.returnStatusNone')
  return returnStatusLabel(t, status)
}

function isPackageStatus(value: string): value is PackageStatus {
  return ALL_PACKAGE_STATUSES.includes(value as PackageStatus)
}

function isReturnedStatus(value: string): value is Exclude<ReturnStatus, 'none'> {
  return value !== 'none' && ALL_RETURN_STATUSES.includes(value as ReturnStatus)
}

export function packageStatusLabelOrRaw(t: TFunction<'cases'>, status: string): string {
  return isPackageStatus(status) ? packageStatusLabel(t, status) : status
}

export function returnStatusLabelOrRaw(t: TFunction<'cases'>, status: string): string {
  return isReturnedStatus(status) ? returnStatusLabel(t, status) : status
}
